import { ContentItemViewNodesType } from '../types'

/**
 * Retrieves parent article node of a given content node.
 *
 */
export const getParentArticleNode = (
    currentNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
): Partial<ContentItemViewNodesType> => {
    if (currentNode?.textType === 'Article') {
        return currentNode
    } else {
        const parentNode = contentNodes?.find((item) =>
            currentNode?.dtvContentItemParentId
                ? item?.id === currentNode?.dtvContentItemParentId
                : item?.dtvItemId === currentNode?.dtvParentId
        )
        if (parentNode) {
            return getParentArticleNode(parentNode, contentNodes)
        } else return {}
    }
}

/**
 * Retrieves child nodes of a given content node based on the hierarchical level.
 *
 * @param level - The level in the hierarchy (0 for direct children, 1 or more for deeper levels).
 * @param hoveredNode - The node currently being hovered over; used as a reference for matching children.
 * @param contentNodes - The complete list of content nodes to filter from.
 * @param parentNodes - (Optional) The list of parent nodes to match children against when level > 0.
 * @returns A filtered list of nodes that are children of the hovered or parent nodes.
 */
const getChildContentItemViewNodes = (
    level: number,
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    parentNodes?: Partial<ContentItemViewNodesType>[]
) => {
    if (level === 0) {
        return contentNodes?.filter(
            (item) =>
                item?.dtvParentId === hoveredNode?.dtvItemId ||
                item?.dtvContentItemParentId === hoveredNode?.id
        )
    } else {
        return contentNodes?.filter(
            (item) =>
                parentNodes?.filter(
                    (itemParent) =>
                        item?.dtvParentId === itemParent?.dtvItemId ||
                        item?.dtvContentItemParentId === itemParent?.id
                )?.length !== 0
        )
    }
}

/**
 * Retrieves all child nodes of a given content node.
 *
 */
export const getChildElements = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[]
) => {
    const levelOneChild = getChildContentItemViewNodes(
        0,
        hoveredNode,
        contentNodes
    )
    const levelTwoChild = getChildContentItemViewNodes(
        1,
        hoveredNode,
        contentNodes,
        levelOneChild
    )
    const levelThreeChild = getChildContentItemViewNodes(
        2,
        hoveredNode,
        contentNodes,
        levelTwoChild
    )
    return [...levelOneChild, ...levelTwoChild, ...levelThreeChild]
}
/**
 * Retrieves all parent nodes upto top level
 *
 */

export const findAncestors = (
    contentNodes: Partial<ContentItemViewNodesType>[],
    currentNode: Partial<ContentItemViewNodesType>
) => {
    if (currentNode?.textType === 'Article') return []
    let ancestors = new Array()
    let localNode = currentNode

    while (localNode) {
        const parent = contentNodes.find(
            (item) =>
                item.id === localNode.dtvContentItemParentId ||
                item?.dtvItemId === localNode?.dtvParentId
        )
        if (!parent) break
        if (ancestors) {
            ancestors?.push(parent)
        }
        localNode = parent
    }

    return ancestors
}

/**
 * check if all content element nodes inside parent article node are hidden
 *
 */
export const areAllChildrenInsideArticleHidden = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    if (!isHidden) return false
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const childNodes = getChildElements(articleNode, contentNodes)
    const activeElementNodes = childNodes?.filter(
        (item) => !item?.isHidden && item?.id !== hoveredNode?.id
    )

    return activeElementNodes?.length === 0
}
export const syncVisibilityBetweenBODAndManufacturers = (
    referenceNode: Partial<ContentItemViewNodesType>,
    allNodes: Partial<ContentItemViewNodesType>[],
    shouldBeHidden: boolean
) => {
    const [toggleType, nodeType] = referenceNode?.isBOD
        ? ['isBOD', 'isManufacturers']
        : ['isManufacturers', 'isBOD']
    return allNodes.map((node) => ({
        ...node,
        isHidden: node?.[nodeType as keyof Partial<ContentItemViewNodesType>]
            ? !shouldBeHidden
            : node?.[toggleType as keyof Partial<ContentItemViewNodesType>]
            ? shouldBeHidden
            : node?.isHidden,
    }))
}
/**
 * Finds all related child nodes up to three levels deep from the hovered node
 * and updates their visibility based on the `isHidden` flag.
 *
 * @param hoveredNode - The content node that is the current point of reference.
 * @param contentNodes - The complete list of content nodes.
 * @param isHidden - Flag indicating whether the matched nodes should be hidden or shown.
 * @returns A new array of content nodes with `isHidden` updated for the matched nodes.
 */

export const getAllChildrenFromHoveredNode = (
    hoveredNode: Partial<ContentItemViewNodesType>,
    contentNodes: Partial<ContentItemViewNodesType>[],
    isHidden: boolean
) => {
    //check for manufacturer/BOD paragraph
    if (hoveredNode?.isBOD || hoveredNode?.isManufacturers) {
        return syncVisibilityBetweenBODAndManufacturers(
            hoveredNode,
            contentNodes,
            isHidden
        )
    }
    const nodesToUpdate = getChildElements(hoveredNode, contentNodes)
    const articleNode = getParentArticleNode(hoveredNode, contentNodes)
    const parentAncestorNodes = !isHidden
        ? findAncestors(contentNodes, hoveredNode)
        : []
    let updatedNodes = contentNodes.map((item) => {
        const shouldUpdate =
            parentAncestorNodes?.some((child) => child?.id === item?.id) ||
            nodesToUpdate.some((child) => child?.id === item?.id) ||
            item?.id === hoveredNode?.id ||
            (!isHidden &&
                (item?.id === articleNode?.id ||
                    item?.dtvItemId === hoveredNode?.dtvParentId ||
                    item?.id === hoveredNode.dtvContentItemParentId))
        return shouldUpdate ? { ...item, isHidden } : item
    })
    // ensure BOD/Manufacturer selection options also reflect visibility state
    const specialNodes = nodesToUpdate.filter(
        (node) => node?.isBOD || node?.isManufacturers
    )
    for (const bodNode of specialNodes) {
        updatedNodes = syncVisibilityBetweenBODAndManufacturers(
            bodNode,
            updatedNodes,
            isHidden
        )
    }
    // hide article if all its children are hidden
    const shouldHideArticle =
        isHidden &&
        areAllChildrenInsideArticleHidden(hoveredNode, updatedNodes, isHidden)
    if (shouldHideArticle && articleNode?.id !== hoveredNode?.id) {
        updatedNodes = updatedNodes.map((item) =>
            item?.id === articleNode?.id ? { ...item, isHidden: true } : item
        )
    }
    return updatedNodes
}
